import type { CompanionAnimation, CompanionManifest, RawPetJson } from './types';
import { rowNamesForGrid } from './row-taxonomy';

const DEFAULT_FRAME_WIDTH = 192;
const DEFAULT_FRAME_HEIGHT = 208;
const DEFAULT_COLUMNS = 8;
const DEFAULT_FRAMES_PER_STATE = 6;
const DEFAULT_LOOP_MS = 1100;

interface NormalizeOptions {
  spritesheet: string;
  atlasWidth: number;
  atlasHeight: number;
  fallbackId: string;
}

export function normalizeManifest(
  raw: RawPetJson,
  { spritesheet, atlasWidth, atlasHeight, fallbackId }: NormalizeOptions,
): CompanionManifest {
  const width = raw.frameWidth ?? DEFAULT_FRAME_WIDTH;
  const height = raw.frameHeight ?? DEFAULT_FRAME_HEIGHT;

  const maxColumns = atlasWidth > 0 ? Math.max(1, Math.floor(atlasWidth / width)) : null;
  const columns = raw.columns !== undefined
    ? (maxColumns !== null ? Math.min(raw.columns, maxColumns) : raw.columns)
    : (maxColumns ?? DEFAULT_COLUMNS);
  const maxRows = atlasHeight > 0 ? Math.max(1, Math.floor(atlasHeight / height)) : null;
  const rows = raw.rows !== undefined
    ? (maxRows !== null ? Math.min(raw.rows, maxRows) : raw.rows)
    : (maxRows ?? 1);

  const framesPerState = Math.min(
    raw.framesPerState ?? DEFAULT_FRAMES_PER_STATE,
    columns,
  );

  const animations: Record<string, CompanionAnimation> = {};
  if (raw.animations) {
    for (const [name, entry] of Object.entries(raw.animations)) {
      if (entry.row >= rows) continue;
      // Frames are bounded by the atlas the same way rows are: a manifest
      // claiming more cells than the sheet has columns would sample past its
      // right edge.
      animations[name] = {
        row: entry.row,
        frames: Math.min(entry.frames ?? framesPerState, columns),
      };
    }
  } else {
    rowNamesForGrid(rows).slice(0, rows).forEach((name, row) => {
      animations[name] = { row, frames: framesPerState };
    });
  }

  const id = raw.id ?? fallbackId;

  return {
    id,
    displayName: raw.displayName ?? id,
    description: raw.description,
    spritesheet,
    frame: { width, height, columns, rows },
    loopMs: raw.loopMs ?? DEFAULT_LOOP_MS,
    animations,
  };
}
